import { type FormEvent, useState } from 'react'
import { useQuery } from '@apollo/client/react'
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  Clock3,
  Home as HomeIcon,
  KeyRound,
  MapPin,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Truck,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import Button from '../components/ui/Button'
import { SERVICE_CATEGORIES_QUERY, type ServiceCategoriesData } from '../graphql/services'
import { localized, localizedDescription, normalizeLocale, type SupportedLocale } from '../lib/i18n'

const translations = {
  el: {
    heroTitle: 'Αξιόπιστος Καθαρισμός Σπιτιού στην Αθήνα',
    heroSubtitle: 'Κλείσε επαγγελματία καθαριστή σε 60 δευτερόλεπτα',
    heroBadge: 'Ελεγμένοι επαγγελματίες σε όλη την πόλη',
    heroCardTitle: 'Γρήγορη κράτηση',
    heroCardBody: 'Αναζήτησε υπηρεσία, διάλεξε ώρα και κλείσε χωρίς τηλεφωνήματα.',
    searchLabel: 'Γρήγορη αναζήτηση',
    postalCodeLabel: 'Ταχυδρομικός κώδικας',
    postalCodePlaceholder: 'π.χ. 11527',
    serviceTypeLabel: 'Τύπος υπηρεσίας',
    serviceTypePlaceholder: 'Επίλεξε υπηρεσία',
    searchCta: 'Αναζήτηση',
    categoriesEyebrow: 'Υπηρεσίες',
    categoriesTitle: 'Κατηγορίες καθαρισμού για κάθε ανάγκη',
    categoriesBody: 'Διάλεξε την υπηρεσία που ταιριάζει στο σπίτι, το Airbnb ή τον επαγγελματικό σου χώρο.',
    servicesCount: 'υπηρεσίες',
    loadingServices: 'Φόρτωση υπηρεσιών...',
    loadingCategories: 'Φορτώνουμε τις διαθέσιμες κατηγορίες.',
    categoriesError: 'Δεν ήταν δυνατή η φόρτωση των κατηγοριών αυτή τη στιγμή.',
    categoriesRetry: 'Δοκίμασε ξανά σε λίγο.',
    viewCategory: 'Δες διαθέσιμες επιλογές',
    howItWorksEyebrow: 'Πώς λειτουργεί',
    howItWorksTitle: 'Από την αναζήτηση στην καθαριότητα, χωρίς τριβές',
    trustEyebrow: 'Γιατί Helply',
    trustTitle: 'Σχεδιασμένο για εμπιστοσύνη από την πρώτη επίσκεψη',
    ctaTitle: 'Έτοιμοι να βρείτε καθαριστή;',
    ctaBody: 'Σύγκρινε υπηρεσίες, βρες τη σωστή επιλογή για τον χώρο σου και προχώρησε στην επόμενη κράτηση.',
    findCleaner: 'Βρες καθαριστή',
    joinProvider: 'Γίνε συνεργάτης',
  },
  en: {
    heroTitle: 'Trusted Home Cleaning in Athens',
    heroSubtitle: 'Book professional cleaners in 60 seconds',
    heroBadge: 'Verified professionals across the city',
    heroCardTitle: 'Booking in minutes',
    heroCardBody: 'Search the right service, pick a time, and book without back-and-forth calls.',
    searchLabel: 'Quick search',
    postalCodeLabel: 'Postal code',
    postalCodePlaceholder: 'e.g. 11527',
    serviceTypeLabel: 'Service type',
    serviceTypePlaceholder: 'Choose a service',
    searchCta: 'Search',
    categoriesEyebrow: 'Services',
    categoriesTitle: 'Cleaning categories for every kind of space',
    categoriesBody: 'Pick the service that fits your home, Airbnb, or commercial property.',
    servicesCount: 'services',
    loadingServices: 'Loading services...',
    loadingCategories: 'Loading available categories.',
    categoriesError: 'We could not load categories right now.',
    categoriesRetry: 'Please try again shortly.',
    viewCategory: 'See available options',
    howItWorksEyebrow: 'How it works',
    howItWorksTitle: 'From search to spotless in three simple steps',
    trustEyebrow: 'Why Helply',
    trustTitle: 'Built to feel trustworthy from the first visit',
    ctaTitle: 'Ready to book?',
    ctaBody: 'Compare services, find the right fit for your space, and move to your next booking faster.',
    findCleaner: 'Find a Cleaner',
    joinProvider: 'Join as a Provider',
  },
} as const

const categoryIcons = {
  home: HomeIcon,
  key: KeyRound,
  briefcase: Briefcase,
  sparkles: Sparkles,
  truck: Truck,
}

function CategoryIcon({ icon }: { icon: string | null }) {
  const Icon = icon ? categoryIcons[icon as keyof typeof categoryIcons] : undefined

  if (!Icon) {
    return <span className="text-3xl leading-none">🧹</span>
  }

  return <Icon className="h-7 w-7 text-primary-600" />
}

function serviceCountLabel(count: number, locale: SupportedLocale) {
  return `${count} ${translations[locale].servicesCount}`
}

export default function Home() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { locale: appLocale } = useLang()
  const activeLocale = normalizeLocale(currentUser?.locale ?? appLocale)
  const copy = useScopedTranslations('landing', translations[activeLocale], activeLocale)

  const [postalCode, setPostalCode] = useState('')
  const [serviceTypeId, setServiceTypeId] = useState('')

  const { data, loading, error } = useQuery<ServiceCategoriesData>(SERVICE_CATEGORIES_QUERY)
  const categories = data?.serviceCategories ?? []

  const serviceTypeGroups = categories.map((category) => ({
    categoryId: category.id,
    categoryName: localized(category, activeLocale),
    serviceTypes: category.serviceTypes.map((serviceType) => ({
      id: serviceType.id,
      name: localized(serviceType, activeLocale),
    })),
  }))

  const steps = [
    {
      icon: MapPin,
      title: activeLocale === 'el' ? 'Αναζήτηση' : 'Search',
      body: activeLocale === 'el' ? 'Βρες καθαριστές στην περιοχή σου.' : 'Find cleaners in your area.',
    },
    {
      icon: CalendarDays,
      title: activeLocale === 'el' ? 'Κράτηση' : 'Book',
      body: activeLocale === 'el' ? 'Διάλεξε ημέρα και ώρα που σε βολεύει.' : 'Pick the day and time that works for you.',
    },
    {
      icon: Sparkles,
      title: activeLocale === 'el' ? 'Χαλάρωσε' : 'Relax',
      body: activeLocale === 'el' ? 'Το σπίτι σου μένει καθαρό και έτοιμο.' : 'Your home is left spotless and ready.',
    },
  ]

  const trustCards = [
    {
      icon: ShieldCheck,
      title: activeLocale === 'el' ? 'Ελεγμένοι καθαριστές' : 'Verified cleaners',
      body: activeLocale === 'el' ? 'Τα προφίλ περνούν από έλεγχο πριν εμφανιστούν στην πλατφόρμα.' : 'Profiles are reviewed before they appear on the platform.',
    },
    {
      icon: Clock3,
      title: activeLocale === 'el' ? 'Ξεκάθαρη τιμολόγηση' : 'Transparent pricing',
      body: activeLocale === 'el' ? 'Ξέρεις τι κλείνεις πριν προχωρήσεις στο επόμενο βήμα.' : 'Know what you are booking before you move to the next step.',
    },
    {
      icon: Smartphone,
      title: activeLocale === 'el' ? 'Εύκολη κράτηση' : 'Easy booking',
      body: activeLocale === 'el' ? 'Η εμπειρία λειτουργεί καθαρά και γρήγορα από κινητό ή desktop.' : 'The booking flow stays clean and fast on mobile or desktop.',
    },
    {
      icon: Star,
      title: activeLocale === 'el' ? 'Αξιολογήσεις & κριτικές' : 'Rated & reviewed',
      body: activeLocale === 'el' ? 'Η εμπιστοσύνη χτίζεται με διαφάνεια και κοινωνική απόδειξη.' : 'Trust grows through transparent ratings and reviews.',
    },
  ]

  function handleQuickSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const params = new URLSearchParams()

    if (postalCode.trim()) params.set('postalCode', postalCode.trim())
    if (serviceTypeId) params.set('serviceType', serviceTypeId)

    navigate({
      pathname: '/search',
      search: params.toString() ? `?${params.toString()}` : '',
    })
  }

  return (
    <div className="overflow-x-hidden">
      <section className="relative isolate overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.18),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.18),_transparent_30%),linear-gradient(135deg,_#eff6ff_0%,_#f8fafc_42%,_#ecfdf5_100%)]">
        <div className="absolute inset-x-0 top-0 h-px bg-white/70" />
        <div className="absolute left-1/2 top-12 h-64 w-64 -translate-x-1/2 rounded-full bg-white/30 blur-3xl" />
        <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl gap-12 px-4 py-14 md:px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,420px)] lg:items-center lg:py-20">
          <div className="relative z-10 max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/75 px-4 py-2 text-sm font-semibold text-primary-700 shadow-sm backdrop-blur">
              <ShieldCheck className="h-4 w-4" />
              {copy.heroBadge}
            </span>
            <h1 className="mt-6 max-w-xl text-5xl font-black tracking-tight text-slate-950 sm:text-6xl">
              {copy.heroTitle}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600 sm:text-xl">
              {copy.heroSubtitle}
            </p>

            <form
              onSubmit={handleQuickSearch}
              className="mt-10 rounded-[1.75rem] border border-white/80 bg-white/90 p-4 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.28)] backdrop-blur"
            >
              <p className="mb-4 text-sm font-semibold text-slate-700">{copy.searchLabel}</p>
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto]">
                <div>
                  <label htmlFor="home-postal-code" className="sr-only">
                    {copy.postalCodeLabel}
                  </label>
                  <input
                    id="home-postal-code"
                    type="text"
                    data-cy="home-postal-code"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder={copy.postalCodePlaceholder}
                    value={postalCode}
                    onChange={(event) => setPostalCode(event.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-medium text-slate-900 outline-none transition focus:border-primary-500 focus:bg-white focus:ring-4 focus:ring-primary-100"
                  />
                </div>

                <div>
                  <label htmlFor="home-service-type" className="sr-only">
                    {copy.serviceTypeLabel}
                  </label>
                  <select
                    id="home-service-type"
                    data-cy="home-service-type"
                    value={serviceTypeId}
                    onChange={(event) => setServiceTypeId(event.target.value)}
                    className="w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-medium text-slate-900 outline-none transition focus:border-primary-500 focus:bg-white focus:ring-4 focus:ring-primary-100"
                    disabled={loading}
                  >
                    <option value="">{loading ? copy.loadingServices : copy.serviceTypePlaceholder}</option>
                    {serviceTypeGroups.map((group) => (
                      <optgroup key={group.categoryId} label={group.categoryName}>
                        {group.serviceTypes.map((serviceType) => (
                          <option key={serviceType.id} value={serviceType.id}>
                            {serviceType.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <Button type="submit" size="lg" className="h-full min-h-[54px] rounded-2xl px-6" data-cy="home-search-submit">
                  {copy.searchCta}
                </Button>
              </div>
            </form>
          </div>

          <div className="relative z-10">
            <div className="rounded-[2rem] border border-white/70 bg-white/80 p-5 shadow-[0_24px_70px_-30px_rgba(15,23,42,0.35)] backdrop-blur">
              <div className="rounded-[1.5rem] bg-slate-950 px-6 py-7 text-white">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-200">
                    Helply
                  </span>
                  <Star className="h-5 w-5 text-amber-300" />
                </div>
                <h2 className="mt-6 text-2xl font-bold">{copy.heroCardTitle}</h2>
                <p className="mt-3 max-w-sm text-sm leading-7 text-slate-300">
                  {copy.heroCardBody}
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-300">60 sec</p>
                    <p className="mt-2 text-sm font-medium text-white">
                      {activeLocale === 'el' ? 'Μέχρι την κράτηση' : 'To book'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-300">100%</p>
                    <p className="mt-2 text-sm font-medium text-white">
                      {activeLocale === 'el' ? 'Καθαρή εμπειρία' : 'Clear flow'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-300">24/7</p>
                    <p className="mt-2 text-sm font-medium text-white">
                      {activeLocale === 'el' ? 'Έτοιμο για αναζήτηση' : 'Ready to search'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
            {copy.categoriesEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            {copy.categoriesTitle}
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">
            {copy.categoriesBody}
          </p>
        </div>

        {loading && categories.length === 0 && (
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="h-12 w-12 animate-pulse rounded-2xl bg-slate-100" />
                <div className="mt-6 h-6 w-2/3 animate-pulse rounded-full bg-slate-100" />
                <div className="mt-4 space-y-2">
                  <div className="h-4 animate-pulse rounded-full bg-slate-100" />
                  <div className="h-4 w-5/6 animate-pulse rounded-full bg-slate-100" />
                </div>
                <div className="mt-8 h-4 w-24 animate-pulse rounded-full bg-slate-100" />
              </div>
            ))}
            <p className="sr-only">{copy.loadingCategories}</p>
          </div>
        )}

        {!loading && error && categories.length === 0 && (
          <div className="mt-10 rounded-[1.75rem] border border-red-100 bg-red-50 px-6 py-5 text-red-700">
            <p className="font-semibold">{copy.categoriesError}</p>
            <p className="mt-1 text-sm">{copy.categoriesRetry}</p>
          </div>
        )}

        {categories.length > 0 && (
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/search?category=${category.id}`}
                className="group rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm transition duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_22px_50px_-28px_rgba(37,99,235,0.35)]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50">
                    <CategoryIcon icon={category.icon} />
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {serviceCountLabel(category.serviceTypes.length, activeLocale)}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-bold text-slate-950">
                  {localized(category, activeLocale)}
                </h3>
                <p className="mt-3 min-h-16 text-sm leading-7 text-slate-600">
                  {localizedDescription(category, activeLocale)}
                </p>
                <div className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary-700">
                  {copy.viewCategory}
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="border-y border-slate-200/80 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
            {copy.howItWorksEyebrow}
          </p>
          <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            {copy.howItWorksTitle}
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step.title} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-primary-600 shadow-sm">
                    <step.icon className="h-6 w-6" />
                  </div>
                  <span className="text-sm font-semibold text-slate-400">0{index + 1}</span>
                </div>
                <h3 className="mt-6 text-xl font-bold text-slate-950">{step.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
              {copy.trustEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              {copy.trustTitle}
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {trustCards.map((card) => (
              <div key={card.title} className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600">
                  <card.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-slate-950">{card.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 md:px-6">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-10 text-white shadow-[0_28px_80px_-32px_rgba(15,23,42,0.65)] sm:px-10 sm:py-12">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{copy.ctaTitle}</h2>
              <p className="mt-4 text-base leading-8 text-slate-300 sm:text-lg">
                {copy.ctaBody}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/search"
                className="inline-flex items-center justify-center rounded-2xl bg-white px-6 py-3.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                {copy.findCleaner}
              </Link>
              <Link
                to="/sign-up/provider"
                className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                {copy.joinProvider}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
